import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X } from "lucide-react";
import { cn } from "@/lib/cn";

export interface ColumnDef {
  key: string;
  label: string;
  /** "number" | "bool" | "select:option1|option2" | "text" | "readonly" */
  type: string;
  /** For readonly columns, optional formatter. */
  render?: (value: unknown) => React.ReactNode;
}

interface Props {
  title: string;
  description: string;
  endpoint: string;            // e.g. "/api/settings/lead-scoring-rules"
  idKey?: string;              // default "id"
  columns: ColumnDef[];
  /** When non-null, allowedTasks restricts a dropdown for the "task" field. */
  extraData?: Record<string, unknown>;
}

export function SettingsTable({
  title,
  description,
  endpoint,
  idKey = "id",
  columns,
}: Props) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["settings", endpoint],
    queryFn: () => api.get<unknown[]>(endpoint),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.put(`${endpoint}/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", endpoint] });
      setEditingId(null);
      setDraft({});
    },
  });

  const rows = (data ?? []) as Record<string, unknown>[];

  function startEdit(row: Record<string, unknown>) {
    setEditingId(row[idKey] as string);
    const d: Record<string, unknown> = {};
    for (const c of columns) if (c.type !== "readonly") d[c.key] = row[c.key];
    setDraft(d);
  }
  function cancelEdit() {
    setEditingId(null);
    setDraft({});
  }
  function save(id: string) {
    updateMut.mutate({ id, patch: draft });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-400">No rows.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="px-3 py-2 text-left font-semibold">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = row[idKey] as string;
                  const isEditing = editingId === id;
                  return (
                    <tr key={id} className="border-b border-slate-100 hover:bg-slate-50">
                      {columns.map((c) => (
                        <td key={c.key} className="px-3 py-2 align-middle">
                          {isEditing && c.type !== "readonly" ? (
                            <EditCell
                              type={c.type}
                              value={draft[c.key]}
                              onChange={(v) => setDraft({ ...draft, [c.key]: v })}
                            />
                          ) : (
                            <ReadCell type={c.type} value={row[c.key]} render={c.render} />
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              onClick={() => save(id)}
                              disabled={updateMut.isPending}
                            >
                              {updateMut.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Check className="h-3.5 w-3.5" />
                              )}
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReadCell({
  type,
  value,
  render,
}: {
  type: string;
  value: unknown;
  render?: (v: unknown) => React.ReactNode;
}) {
  if (render) return <>{render(value)}</>;
  if (value === null || value === undefined) return <span className="text-slate-300">-</span>;
  if (type === "bool") {
    const isOn = value === 1 || value === true;
    return (
      <span
        className={cn(
          "inline-flex h-5 w-9 items-center rounded-full px-0.5",
          isOn ? "bg-emerald-500" : "bg-slate-300",
        )}
      >
        <span
          className={cn(
            "h-4 w-4 rounded-full bg-white shadow",
            isOn && "translate-x-4",
            "transition-transform",
          )}
        />
      </span>
    );
  }
  if (type === "number") return <span className="font-mono tabular-nums">{String(value)}</span>;
  if (type.startsWith("select:")) {
    return <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{String(value)}</span>;
  }
  return <span>{String(value)}</span>;
}

function EditCell({
  type,
  value,
  onChange,
}: {
  type: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (type === "bool") {
    const isOn = value === 1 || value === true;
    return (
      <button
        type="button"
        onClick={() => onChange(!isOn)}
        className={cn(
          "inline-flex h-5 w-9 items-center rounded-full px-0.5 transition-colors",
          isOn ? "bg-emerald-500" : "bg-slate-300",
        )}
      >
        <span
          className={cn(
            "h-4 w-4 rounded-full bg-white shadow",
            isOn && "translate-x-4",
            "transition-transform",
          )}
        />
      </button>
    );
  }
  if (type === "number") {
    return (
      <input
        type="number"
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="w-24 rounded border border-slate-300 px-2 py-1 text-sm tabular-nums"
      />
    );
  }
  if (type.startsWith("select:")) {
    const options = type.slice("select:".length).split("|");
    return (
      <select
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-slate-300 px-2 py-1 text-sm"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="text"
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
    />
  );
}
